import { FC } from 'react';
import { useIntl } from 'react-intl';
import { Bar } from 'react-chartjs-2';
import { ChartData, ChartOptions } from 'chart.js';

import {
  GRID_COLOR,
  STATE_COLORS,
  SURFACE_COLOR,
} from '../../constants/subscriberCohorts';
import {
  COHORT_STATES,
  Cohort,
  CohortState,
  formatInteger,
  formatPercent,
  monthKey,
} from '../../models/SubscriberCohortsModel';

const CHART_HEIGHT_PX = 340;
const MAX_BAR_THICKNESS_PX = 24;
const SEGMENT_GAP_PX = 2;

const STATE_LABEL_ID: Record<CohortState, string> = {
  active: 'stateActive',
  paused: 'statePaused',
  cancelled: 'stateCancelled',
  other: 'stateOther',
};

type Props = {
  cohorts: Cohort[];
};

// Size of each cohort split by the state its plans are in today: how much of
// every month's intake is still subscribed.
const CohortStatusChart: FC<Props> = ({ cohorts }) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `subscriberCohorts.${id}` }, values);

  const data: ChartData<'bar'> = {
    labels: cohorts.map((c) => monthKey(c.month)),
    datasets: COHORT_STATES.map((state) => ({
      label: t(STATE_LABEL_ID[state]),
      data: cohorts.map((c) => c.states[state]),
      backgroundColor: STATE_COLORS[state],
      // Surface-coloured gap between stacked segments.
      borderColor: SURFACE_COLOR,
      borderWidth: { top: SEGMENT_GAP_PX },
      borderSkipped: false,
      maxBarThickness: MAX_BAR_THICKNESS_PX,
    })),
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: {
        stacked: true,
        grid: { color: GRID_COLOR },
        ticks: { callback: (value) => formatInteger(Number(value)) },
      },
    },
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (item) => {
            const size = cohorts[item.dataIndex].size;
            const count = item.parsed.y;
            return `${item.dataset.label}: ${formatInteger(count)} (${formatPercent(
              size ? count / size : null
            )})`;
          },
          footer: (items) =>
            t('cohortSize', {
              count: formatInteger(cohorts[items[0].dataIndex].size),
            }),
        },
      },
    },
  };

  return (
    <div>
      <div className="fs-5 fw-bold mb-1">{t('statusChart')}</div>
      <div className="text-muted fs-8 mb-3">{t('statusChartHelp')}</div>
      <div style={{ height: `${CHART_HEIGHT_PX}px` }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

export default CohortStatusChart;
